import { useTrans } from '@ui/i18n/use-trans';
import { useSettings } from '@ui/settings/use-settings';
import { Link } from 'react-router-dom';
import { NavbarProps } from '@common/ui/navigation/navbar/navbar';
import clsx from 'clsx';
import { useThemeSelector } from '@ui/themes/theme-selector-context';
import { getThemeLogo } from '@ui/themes/utils/get-theme-logo';

interface LogoProps {
  color?: NavbarProps['color'];
  logoColor?: NavbarProps['logoColor'];
  isDarkMode?: boolean;
  className?: string;
}
export function Logo({ color, logoColor, isDarkMode, className }: LogoProps) {
  const { trans } = useTrans();
  const { branding } = useSettings();
  const { selectedTheme } = useThemeSelector();

  const themeLogo = getThemeLogo(selectedTheme, isDarkMode);

  let desktopLogo: string | undefined;
  let mobileLogo: string | undefined;
  if (themeLogo) {
    desktopLogo = themeLogo;
    mobileLogo = themeLogo;
  } else {
    const shouldUseLightLogo =
      isDarkMode ||
      !branding.logo_dark ||
      (logoColor !== 'dark' && color !== 'bg' && color !== 'bg-alt');

    if (shouldUseLightLogo) {
      desktopLogo = branding.logo_light;
      mobileLogo = branding.logo_light_mobile || desktopLogo;
    } else {
      desktopLogo = branding.logo_dark || branding.logo_light;
      mobileLogo = branding.logo_dark_mobile || desktopLogo;
    }
  }

  if (!mobileLogo && !desktopLogo) {
    return null;
  }

  return (
    <Link
      to="/"
      className={clsx(
        'mr-4 block h-full max-h-26 flex-shrink-0 md:mr-24 md:max-h-36',
        className,
      )}
      data-logo="navbar"
      aria-label={trans({ message: 'Go to homepage' })}
    >
      <picture>
        <source srcSet={mobileLogo || desktopLogo} media="(max-width: 768px)" />
        <source srcSet={desktopLogo} media="(min-width: 768px)" />
        <img
          src={desktopLogo || mobileLogo}
          className="block h-full max-h-26 w-auto md:max-h-36"
          alt={trans({ message: 'Site logo' })}
        />
      </picture>
    </Link>
  );
}
